<template>
  <!-- Fits the 1024px billboard on narrow screens without changing its
       coordinates: an overview scaled to the screen width, and a tap zooms
       into a pannable 1:1 view. Placing an image always uses the 1:1 view, so
       the placement maths (getBoundingClientRect deltas, 16px steps) is the
       same as on desktop. On wide screens it renders the billboard untouched. -->
  <div class="bb-wrap" v-bind:class="{ narrow: narrow, zoomed: isZoomed }">
    <div class="bb-viewport" ref="viewport" v-bind:style="viewportStyle">
      <div class="bb-stage" v-bind:style="stageStyle">
        <slot></slot>
      </div>
    </div>
    <div v-if="narrow && !isZoomed" class="bb-tap" v-on:click="zoomAt" title="Tap to zoom in"></div>
    <button v-if="narrow && isZoomed && !locked" type="button" class="btn btn-light bb-zoom-out" v-on:click="zoomOut" aria-label="Zoom out to the whole billboard">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
        <path fill-rule="evenodd" d="M6.5 12a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11M13 6.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0"/>
        <path d="M10.344 11.742q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1 6.5 6.5 0 0 1-1.398 1.4z"/>
        <path fill-rule="evenodd" d="M3 6.5a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5"/>
      </svg>
      Zoom out
    </button>
  </div>
</template>

<script>
const BILLBOARD_SIZE = 1024;
// Below this viewport width the billboard (plus margins) doesn't fit.
const NARROW_BELOW = BILLBOARD_SIZE + 32;
// Layout viewport size; unlike window.inner*, not affected by pinch zoom.
const viewportWidth = () => document.documentElement.clientWidth || window.innerWidth;
const viewportHeight = () => document.documentElement.clientHeight || window.innerHeight;

export default {
  name: 'billboard-viewport',
  props: {
    // True while an image is being placed: stay in the 1:1 view.
    locked: { type: Boolean, default: false },
  },
  data() {
    return {
      narrow: viewportWidth() < NARROW_BELOW,
      zoomed: false,
      scale: 1,
      zoomedHeight: BILLBOARD_SIZE,
    };
  },
  computed: {
    isZoomed() {
      return this.zoomed || this.locked;
    },
    viewportStyle() {
      if (!this.narrow) return {};
      if (this.isZoomed) {
        return { height: this.zoomedHeight + 'px', overflow: 'auto' };
      }
      return { height: Math.round(BILLBOARD_SIZE * this.scale) + 'px', overflow: 'hidden' };
    },
    stageStyle() {
      if (!this.narrow || this.isZoomed) return {};
      return { transform: `scale(${this.scale})` };
    },
  },
  watch: {
    locked(isLocked) {
      if (isLocked) {
        this.zoomed = true;
      }
    },
  },
  methods: {
    measure() {
      this.narrow = viewportWidth() < NARROW_BELOW;
      const width = this.$el ? this.$el.clientWidth : viewportWidth();
      this.scale = Math.min(1, width / BILLBOARD_SIZE);
      this.zoomedHeight = Math.min(BILLBOARD_SIZE, Math.round(viewportHeight() * 0.7));
    },
    zoomAt(e) {
      const rect = this.$refs.viewport.getBoundingClientRect();
      const x = (e.clientX - rect.left) / this.scale;
      const y = (e.clientY - rect.top) / this.scale;
      this.zoomed = true;
      this.$nextTick(() => {
        const vp = this.$refs.viewport;
        vp.scrollLeft = x - vp.clientWidth / 2;
        vp.scrollTop = y - vp.clientHeight / 2;
      });
    },
    zoomOut() {
      this.zoomed = false;
      this.$refs.viewport.scrollLeft = 0;
      this.$refs.viewport.scrollTop = 0;
    },
  },
  mounted() {
    this.measure();
    window.addEventListener('resize', this.measure);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure);
  },
};
</script>

<style scoped>
/* Never let the 1024px billboard widen the page on a phone, even before the
   script has measured (browsers zoom the whole page out if it overflows). */
@media (max-width: 1055px) {
  .bb-wrap {
    width: 100%;
    overflow: hidden;
  }
}
.bb-wrap.narrow {
  position: relative;
  width: 100%;
  margin: 16px 0;
}
.bb-wrap.narrow .bb-viewport {
  width: 100%;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
.bb-wrap.narrow .bb-stage {
  width: 1024px;
  height: 1024px;
  transform-origin: 0 0;
}
.bb-tap {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: zoom-in;
}
.bb-zoom-out {
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 2;
  min-height: 44px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
</style>
